import React, { useState, useEffect, useContext, useRef } from "react";
import { ModalButton } from "../MsgBoxModal/styles";
import { useFormikContext, Field } from "formik";
import {
	GET_CONTACTS_WITH_TAG,
	GET_PRESUB_FROM_IDS,
	GET_CONTACTS_WITHOUT_TAG,
} from "./query";
import client from "../../Apollo";
import { Label, msgBoxSelectCustomStyles } from "../../Styles/global";
import Select from "react-select";
import { useTheme } from "styled-components";
import { pt } from "date-fns/locale";
import { ObjectCanon } from "@apollo/client/cache/inmemory/object-canon";
import Tooltip from "../Tooltip/tooltip.component";

const AdvancedTagDialog = ({
	open,
	setOpen,
	title,
	children,
	MsgBoxSelect,
	canBulkBroadcast,
	loadTagsOptions,
	removedPreSubsCount,
	preSubAsSub,
	setPreSubList,
	setTagSplitVisible,
	preSubCount,
	setPreSubCount,
	tagSplitVisible,
	preSubList,
	advancedSaved,
	setAdvancedSaved,
	advancedSendContacts,
	setAdvancedSendContacts,
	setTagsContactsCount,
}) => {
	const { setFieldValue, values, errors, setFieldTouched } =
		useFormikContext();

	const msgBoxTheme = useTheme();

	const [filterLayers, setFilterLayers] = useState(values.tagFilter);
	let originalFormValues = useRef();

	useEffect(() => {
		if (open) {
			// Only set if it hasn't been set before or if we just saved successfully
			if (!originalFormValues.current || advancedSaved) {
				originalFormValues.current = structuredClone(filterLayers);
			}
		}
	}, [open, advancedSaved]);

	useEffect(() => {
		setFieldValue("tagFilter", filterLayers);
	}, [filterLayers]);

	useEffect(() => {
		if (filterLayers && values?.account?.value) {
			filterTags();
		}
	}, [filterLayers]);

	async function filterTags() {
		// Get the first layer results
		let filteredContacts = await getContacts(filterLayers[0]);
		// For every subsequent layer, use the previous results as a base
		for (let i = 1; i < filterLayers.length; i++) {
			const layer = filterLayers[i];
			let newContacts = await getContacts(layer, filteredContacts);
			// Combine newContacts with filteredContacts based on the filter type
			if (layer?.tagFilter?.value === "Isnt") {
				filteredContacts = filteredContacts.filter(
					(contact) =>
						!newContacts.some(
							(nc) => nc.ContactId === contact.ContactId
						)
				);
			} else if (layer?.tagFilter?.value === "And") {
				filteredContacts = filteredContacts.filter((contact) =>
					newContacts.some((nc) => nc.ContactId === contact.ContactId)
				);
			} else if (layer?.tagFilter?.value === "Or") {
				filteredContacts = [...filteredContacts, ...newContacts].filter(
					(contact, index, self) =>
						index ===
						self.findIndex((c) => c.ContactId === contact.ContactId)
				);
			}
		}
		setAdvancedSendContacts(filteredContacts);
	}

	useEffect(() => {
		if (preSubAsSub === false) {
			filterPreSubs();
		}
		if (advancedSendContacts) {
			setTagsContactsCount(advancedSendContacts.length);
		}
	}, [filterLayers, advancedSendContacts]);

	useEffect(() => {
		if (preSubList) setPreSubCount(uniquePreSubs(preSubList));
	}, [preSubList]);

	const addFilterLayer = () => {
		setFilterLayers([
			...filterLayers,
			{
				id: Date.now(),
				tagFilter: { label: "Or", value: "Or" },
				selectedTags: [],
			},
		]);
	};

	const removeFilterLayer = (id) => {
		setFilterLayers(filterLayers.filter((layer) => layer.id !== id));
	};

	const updateFilterLayer = (id, key, value) => {
		setFilterLayers(
			filterLayers.map((layer) =>
				layer.id === id ? { ...layer, [key]: value } : layer
			)
		);
	};

	function handleSave() {
		if (filterLayers[0].selectedTags.length > 0) {
			originalFormValues.current = structuredClone(filterLayers);
			setFieldTouched("tags", true);
			setFieldTouched("tagFilter", true);
			setAdvancedSaved(true);
			setOpen(false);
		} else {
			setOpen(false);
		}
	}

	function handleCancel() {
		if (originalFormValues.current) {
			setFilterLayers(originalFormValues.current);
		} else {
			setFilterLayers([
				{
					id: 0,
					tagFilter: { label: "Or", value: "Or" },
					selectedTags: [],
				},
			]);
		}
		setOpen(false);
	}

	async function getContacts(filterLayer, baseContacts = null) {
		try {
			const tags = filterLayer.selectedTags.map((tag) =>
				tag.label.replace(/ *\([^)]*\) *|\*/g, "")
			);
			let resultArr = [];

			if (baseContacts) {
				// When baseContacts exist, perform client-side combining:
				if (filterLayer?.tagFilter?.value === "And") {
					// Intersect: for each tag, retain only contacts in baseContacts that have the tag
					let contactsResult = baseContacts;
					for (const tag of filterLayer.selectedTags) {
						const formattedTag = tag.label
							.replace(/\(\d+\s*contacts\)/gi, "")
							.trim();
						const { data } = await client.query({
							query: GET_CONTACTS_WITH_TAG,
							variables: {
								tags: [formattedTag],
								account: values.account.value,
							},
							fetchPolicy: "network-only",
						});
						contactsResult = contactsResult.filter((contact) =>
							data.msgbox_Contact.some(
								(tc) => tc.ContactId === contact.ContactId
							)
						);
					}
					resultArr = contactsResult;
				} else {
					// For Or and Isnt, query the contacts and combine with the base list:
					const { data } = await getTagContacts(
						tags,
						values.account.value,
						filterLayer.tagFilter.value
					);
					const queryResults = data.msgbox_Contact;
					if (filterLayer?.tagFilter?.value === "Or") {
						resultArr = [...baseContacts, ...queryResults].filter(
							(contact, index, self) =>
								index ===
								self.findIndex(
									(c) => c.ContactId === contact.ContactId
								)
						);
					} else if (filterLayer?.tagFilter?.value === "Isnt") {
						resultArr = baseContacts.filter(
							(contact) =>
								!queryResults.some(
									(q) => q.ContactId === contact.ContactId
								)
						);
					}
				}
			} else {
				// No baseContacts means we're processing the first filter layer:
				if (filterLayer?.tagFilter?.value === "Or") {
					const { data } = await getTagContacts(
						tags,
						values.account.value,
						"Or"
					);
					resultArr = data.msgbox_Contact;
				} else if (filterLayer?.tagFilter?.value === "And") {
					let commonContacts = new Set();
					for (let i = 0; i < filterLayer.selectedTags.length; i++) {
						let formattedTag = filterLayer.selectedTags[i].label
							.replace(/\(\d+\s*contacts\)/gi, "")
							.trim();
						const { data } = await client.query({
							query: GET_CONTACTS_WITH_TAG,
							variables: {
								tags: [formattedTag],
								account: values.account.value,
							},
							fetchPolicy: "network-only",
						});
						let contacts = new Set(
							data.msgbox_Contact.map((c) => c.ContactId)
						);
						commonContacts =
							i === 0
								? contacts
								: new Set(
										[...commonContacts].filter((id) =>
											contacts.has(id)
										)
								  );
					}
					const res = await client.query({
						query: GET_PRESUB_FROM_IDS,
						variables: { contactIds: Array.from(commonContacts) },
						fetchPolicy: "network-only",
					});
					resultArr = res.data.msgbox_Contact;
				} else if (filterLayer?.tagFilter?.value === "Isnt") {
					if (tags.length > 0) {
						const { data } = await getTagContacts(
							tags,
							values.account.value,
							"Isnt"
						);
						resultArr = data.msgbox_Contact;
					}
				}
			}
			return resultArr;
		} catch (e) {
			console.error(e);
		}
	}

	async function getTagContacts(tags, account, filterOption) {
		let contactsWithTags;
		if (filterOption === "Or") {
			contactsWithTags = await client.query({
				query: GET_CONTACTS_WITH_TAG,
				variables: {
					tags,
					account,
				},
				fetchPolicy: "network-only",
			});
		} else if (filterOption === "And") {
			let contacts = advancedSendContacts.map((cont) => {
				return cont.ContactId;
			});
			contactsWithTags = await client.query({
				query: GET_PRESUB_FROM_IDS,
				variables: {
					contactIds: contacts,
				},
				fetchPolicy: "network-only",
			});
		} else if (filterOption === "Isnt") {
			contactsWithTags = await client.query({
				query: GET_CONTACTS_WITHOUT_TAG,
				variables: {
					tags,
					account,
				},
				fetchPolicy: "network-only",
			});
		}
		return contactsWithTags;
	}

	async function filterPreSubs() {
		try {
			setPreSubList([]);
			setTagSplitVisible(false);
			let preSubsInitial = advancedSendContacts.filter((contact) => {
				return contact.Status === "Presubscribed";
			});
			let preSubsFinal = [];
			if (preSubsInitial.length > 10 && preSubAsSub === false) {
				preSubsInitial.map((contact) => {
					preSubsFinal.push({
						label: `Remove "${contact.Name}" from recipients`,
						contactId: contact.ContactId,
						value: {
							contactId: contact.ContactId,
						},
					});
				});
				setPreSubList(preSubsFinal);
				setTagSplitVisible(true);
			}
		} catch (e) {
			console.error(e);
		}
	}

	const handleRemovePreSub = async (selected) => {
		const contactToRemove = preSubList.filter(
			(element) => !selected.includes(element)
		);
		let newSendList = advancedSendContacts.filter((contact) => {
			return contact.ContactId !== contactToRemove[0].contactId;
		});
		setAdvancedSendContacts(newSendList);
	};

	// Handle closing when clicking outside the dialog
	const handleOutsideClick = (e) => {
		if (e.target.tagName === "DIALOG") {
			setOpen(false);
		}
	};

	const loadTagFilter = () => {
		return [
			{ label: "Or", value: "Or" },
			{ label: "And", value: "And" },
			{ label: "Isnt", value: "Isnt" },
		];
	};

	function uniquePreSubs(contactList) {
		let uniqueIds = new Set();
		contactList.forEach((preSub) => {
			if (preSub.contactId != null) {
				// Ensure contactId is valid
				uniqueIds.add(preSub.contactId);
			}
		});
		return uniqueIds.size; // Return count of unique IDs
	}

	return (
		<dialog
			open={open}
			onClick={handleOutsideClick}
			style={{
				border: "1px solid",
				background: "white",
				padding: "20px",
				minWidth: "95%",
				borderRadius: "8px",
				position: "fixed",
				top: "50%",
				left: "50%",
				transform: "translate(-50%, -50%)",
				zIndex: 1000,
				maxHeight: 500,
				overflowY: "scroll",
			}}
		>
			<div className="dialog-content">
				<h2>{title}</h2>
				{children}

				<div>
					<div>
						{filterLayers.map((layer) => (
							<div
								key={layer.id}
								style={{
									display: "flex",
									flexDirection: "row",
									gap: 10,
									alignItems: "center",
									justifyContent: "space-between",
									whiteSpace: "pre-line",
								}}
							>
								<Field
									component={MsgBoxSelect}
									name="tagFilter"
									label="Filter"
									hasTooltip
									toolTipText={
										<div
											style={{
												width: "390px",
												whiteSpace: "pre-line",
												padding: 0,
												margin: 0,
												fontSize: 11,
											}}
										>
											<div
												style={{ marginBottom: "12px" }}
											>
												<b
													style={{
														fontSize: "12px",
														color: "#fff",
													}}
												>
													Or:
												</b>
												<br />
												Show contacts with <i>any</i> of
												the tags selected. E.g. if you
												pick "Customer" or "VIP",
												contacts with either tag will be
												selected
											</div>
											<div
												style={{ marginBottom: "12px" }}
											>
												<b
													style={{
														fontSize: "12px",
														color: "#fff",
													}}
												>
													And:
												</b>
												<br />
												Show contacts with <i>
													all
												</i>{" "}
												the selected tags. E.g. if you
												pick 'Customer' and 'VIP', only
												contacts which have both those
												tags will be selected
											</div>
											<div>
												<b
													style={{
														fontSize: "12px",
														color: "#fff",
													}}
												>
													Isn't:
												</b>
												<br />
												Shows contacts that have{" "}
												<i>don't have</i> the selected
												tags. E.g. if you pick 'VIP',
												you'll see all contacts not
												tagged with 'VIP'
											</div>
										</div>
									}
									options={loadTagFilter()}
									value={layer.tagFilter}
									onChange={(selected) => {
										updateFilterLayer(
											layer.id,
											"tagFilter",
											selected
										);
										setFieldValue(
											"tags",
											filterLayers[0].selectedTags
										);
									}}
								/>

								<Field
									component={MsgBoxSelect}
									name="tags"
									label="Select Tag(s) "
									isMulti
									loadOptions={loadTagsOptions} // Proper async loading
									placeholder="Search tags"
									value={layer.selectedTags}
									onChange={(selected) => {
										let total = selected.reduce(
											(acc, item) => acc + item.userCount,
											0
										);
										let newOptions = [...selected];

										if (!canBulkBroadcast && total > 10) {
											newOptions.pop();
										}

										updateFilterLayer(
											layer.id,
											"selectedTags",
											newOptions
										);
									}}
									onBlur={() => {
										setFieldTouched("tags", true);
									}}
									error={errors.tags}
									async
								/>

								{filterLayers.length > 1 && (
									<span
										onClick={() =>
											removeFilterLayer(layer.id)
										}
									>
										➖
									</span>
								)}
							</div>
						))}

						<span onClick={addFilterLayer}>➕</span>
					</div>

					{filterLayers[0].selectedTags.length > 0 ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								marginRight: 10,
								marginBottom: 25,
								marginTop: 25,
							}}
						>
							<b>
								Total:{" "}
								{advancedSendContacts.length -
									removedPreSubsCount}
								{" contacts "}
							</b>

							{preSubList?.length > 10 ? (
								<p
									style={{
										color:
											preSubList.length > 10
												? "red"
												: "black",
									}}
								>
									Presubscribers: {preSubCount}{" "}
								</p>
							) : null}

							<p style={{ fontSize: 9 }}>
								* excludes any duplicates
							</p>
						</div>
					) : null}
				</div>

				{tagSplitVisible === true ? (
					<>
						<Label style={{ color: "red", fontSize: "10px" }}>
							Your current setup limits you to sending to 10
							presubscribers, please remove some to continue to
							send this broadcast.
						</Label>
						<div
							style={{
								maxHeight: "150px",
								overflow: "auto",
							}}
						>
							<Select
								isMulti
								isClearable={false}
								value={preSubList}
								onChange={handleRemovePreSub}
								styles={msgBoxSelectCustomStyles(msgBoxTheme)}
								theme={(theme) => ({
									...theme,
									colors: {
										...theme.colors,
										primary25:
											msgBoxTheme.colours.primaryLight,
										primary: msgBoxTheme.colours.primary,
									},
								})}
							></Select>
						</div>{" "}
					</>
				) : null}

				<div
					style={{
						fontSize: 11,
						display: "flex",
						justifyContent: "center",
						margin: 20,
					}}
				>
					<p>
						Or - Contacts found in <b>any</b> selected tags. <br />
						And - Contacts common to <b>all</b> selected tags.{" "}
						<br />
						Isn't - Contacts that <b>don't have</b> the selected
						tags.
					</p>
				</div>

				<div
					style={{
						marginTop: 50,
						display: "flex",
						alignItems: "center",
						maxWidth: 200,

						position: "relative",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
					}}
				>
					<ModalButton
						buttonName="Cancel"
						secondary
						onClick={() => {
							handleCancel();
						}}
					></ModalButton>
					<ModalButton
						type="button"
						buttonName="Save"
						primary
						onClick={() => {
							handleSave();
						}}
					></ModalButton>
				</div>
			</div>
		</dialog>
	);
};

export default AdvancedTagDialog;
